import { GifDecision } from '@dreryk/egabinet-dashboard-frontend-api';

import { dateFormatter, useTranslate } from '@dreryk/react-common';
import {
   appendTestableElementAttributes,
   DownloadIcon,
   IconSize,
   IconWrapper,
   Link,
   LinkTarget,
   OpenInNewIcon,
   StyledFlex,
   Text,
   TextSeparator,
   TextSeparatorType,
} from '@dreryk/react-components';

import { dashboardTranslationKeys } from '../../translations';

import {
   StyledGifDecisionsCardListElementColumn,
   StyledGifDecisionsCardListElementDateAndEntityName,
   StyledGifDecisionsCardListElementLink,
   StyledGifDecisionsCardListElementWrapper,
} from './GifDecisionsCardListElement.styled';

type GifDecisionCardListElementProps = {
   readonly gifDecision: GifDecision;
};

const formatDecisionType = (type: string): string => {
   const formatted = type.toLowerCase().replace(/_/gu, ' ');
   return formatted.charAt(0).toUpperCase() + formatted.slice(1);
};

export const GifDecisionsCardListElement = (props: GifDecisionCardListElementProps): JSX.Element => {
   const translate = useTranslate();

   const noDataFromGifInfo = translate(dashboardTranslationKeys.cards.noDataFromGif);

   const medicineProductName = props.gifDecision.medicineProductName
      ? `"${props.gifDecision.medicineProductName}"`
      : noDataFromGifInfo;

   const responsibleEntityName = props.gifDecision.responsibleEntityName || noDataFromGifInfo;

   const decisionDate = dateFormatter(props.gifDecision.date);

   const decisionDateAndResponsibleEntityName = `${decisionDate} • ${responsibleEntityName}`;

   return (
      <StyledGifDecisionsCardListElementWrapper {...appendTestableElementAttributes('gifDecision')}>
         <StyledGifDecisionsCardListElementColumn>
            <StyledGifDecisionsCardListElementLink
               href={props.gifDecision.viewLink}
               target='_blank'
               {...appendTestableElementAttributes('nameAndType')}
            >
               <TextSeparator separator={TextSeparatorType.SPACE_HYPHEN_SPACE}>
                  <Text testId={'medicineProductName'}>{medicineProductName}</Text>
                  {props.gifDecision.type ? (
                     <Text testId={'gifDecisionType'}>{formatDecisionType(props.gifDecision.type)}</Text>
                  ) : null}
               </TextSeparator>
            </StyledGifDecisionsCardListElementLink>
            <StyledGifDecisionsCardListElementDateAndEntityName
               {...appendTestableElementAttributes('dateAndEntityName')}
            >
               {decisionDateAndResponsibleEntityName}
            </StyledGifDecisionsCardListElementDateAndEntityName>
         </StyledGifDecisionsCardListElementColumn>
         <StyledFlex justifyContent={'flex-end'}>
            <Link
               testId={'viewLink'}
               icon={<OpenInNewIcon />}
               href={props.gifDecision.viewLink}
               disabled={!props.gifDecision.viewLink}
               target={LinkTarget.BLANK}
            />
            <Link
               testId={'downloadLink'}
               icon={
                  <IconWrapper size={IconSize.SMALL} testId={'addCircleIcon'}>
                     <DownloadIcon testId={'downloadGifDecision'} />
                  </IconWrapper>
               }
               href={props.gifDecision.downloadLink}
               disabled={!props.gifDecision.downloadLink}
            />
         </StyledFlex>
      </StyledGifDecisionsCardListElementWrapper>
   );
};
